/**
 * useReportDslMetadata
 *
 * Single-call bootstrapper for the New Report wizard's dropdowns. Hits
 * `GET /il/analytics/:tenant/:site/report-dsl-metadata` (server-authoritative
 * — see `st-ck-server/.../ReportDslMetadata.ts`) and returns the full
 * payload of aggregation functions, filter operators, time presets,
 * granularities, visualization types, identity fields, and the merged
 * standard-plus-custom field catalog.
 *
 * The wizard never blocks on this call: when `metadata` is `null` the
 * dropdowns silently fall back to the static lists baked into
 * `reportDsl.ts`. So a transient server outage degrades to "old behaviour"
 * rather than an empty dropdown.
 *
 * Module-level cache keyed by `(tenantId, analytics path segment)` keeps
 * subsequent opens of the wizard within the same tab instant. Blank Site ID
 * in IL-Settings maps to the reserved `__all_sites__` path segment.
 */

import { useCallback, useEffect, useState } from 'react';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import {
  AggregationFn, FilterOp, Granularity, TimeRangePreset, VisualizationType,
} from './reportDsl';

export type FieldGroup =
  | 'Identity' | 'Event' | 'Page' | 'Device' | 'Marketing' | 'Time' | 'Geo' | 'Custom';

export interface AggregationFunctionMetadata {
  value: AggregationFn;
  label: string;
  needsField: boolean;
  numericOnly: boolean;
  description?: string;
}

export interface FilterOperatorMetadata {
  value: FilterOp;
  label: string;
  arity: 'none' | 'single' | 'multi';
}

export interface TimePresetMetadata {
  value: TimeRangePreset;
  label: string;
}

export interface GranularityMetadata {
  value: Granularity;
  label: string;
}

export interface VisualizationMetadata {
  value: VisualizationType | string;
  label: string;
  kind: 'time-series' | 'distribution' | 'tabular' | 'single-value' | 'realtime' | 'geo';
  aggregatesOverTime: boolean;
}

export interface IdentityFieldMetadata {
  value: 'user_id' | 'anonymous_id' | 'session_id';
  label: string;
}

export interface FieldMetadata {
  field: string;
  label: string;
  group: FieldGroup;
  allowedForDimension: boolean;
  allowedForMetric: boolean;
  isNumeric: boolean;
  clickHouseType?: string;
}

export interface ReportDslMetadata {
  schemaVersion: number;
  generatedAt: string;
  cacheTtlSeconds: number;
  aggregationFunctions: AggregationFunctionMetadata[];
  filterOperators: FilterOperatorMetadata[];
  timeRangePresets: TimePresetMetadata[];
  granularities: GranularityMetadata[];
  visualizationTypes: VisualizationMetadata[];
  identityFields: IdentityFieldMetadata[];
  fieldCatalog: {
    standard: FieldMetadata[];
    custom: { dimensions: string[]; metrics: string[] };
  };
}

interface CacheEntry {
  payload: ReportDslMetadata;
  fetchedAt: number;
}

// Module-level cache — same lifetime as the JS bundle (i.e. until the user
// reloads the page). Server already caches for 5 minutes; this cache exists
// purely to avoid the network round-trip on every wizard open within the
// same session.
const moduleCache = new Map<string, CacheEntry>();

const cacheKey = (tenantId: string, siteId: string | undefined | null): string =>
  `${tenantId}::${infralytiqsAnalyticsSitePathSegment(siteId)}`;

export interface UseReportDslMetadataResult {
  metadata: ReportDslMetadata | null;
  loading: boolean;
  error: string | null;
  /** Force-refresh — bypasses both client and server caches. */
  refresh: () => Promise<void>;
}

export const useReportDslMetadata = (
  tenantId: string | undefined,
  siteId: string | undefined | null,
): UseReportDslMetadataResult => {
  const [metadata, setMetadata] = useState<ReportDslMetadata | null>(() => {
    if (!tenantId) return null;
    return moduleCache.get(cacheKey(tenantId, siteId))?.payload ?? null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetadata = useCallback(
    async (opts: { refresh?: boolean } = {}): Promise<void> => {
      if (!tenantId) return;
      const pathSeg = infralytiqsAnalyticsSitePathSegment(siteId);
      const key = cacheKey(tenantId, siteId);
      // Hot cache hit on a normal (non-forced) call: nothing to do.
      if (!opts.refresh) {
        const hit = moduleCache.get(key);
        if (hit) {
          setMetadata(hit.payload);
          return;
        }
      }
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ lookbackDays: '14' });
        if (opts.refresh) qs.set('refresh', '1');
        const res: any = await apiClient.get(
          `/il/analytics/${encodeURIComponent(tenantId)}/${encodeURIComponent(pathSeg)}/report-dsl-metadata?${qs.toString()}`,
        );
        // The shared `apiClient.get()` returns the parsed JSON body verbatim
        // (no axios-style `.data` wrapper). Most server endpoints respond
        // with `{ status, message, data }`, but the analytics controllers
        // we hit here return a flat `{ success, metadata, ... }` envelope
        // — accept both shapes so the wizard works regardless of which
        // style the endpoint settles on.
        const body: any = res?.data ?? res;
        const payload: ReportDslMetadata | undefined = body?.metadata;
        if (!payload) {
          throw new Error('Empty metadata payload');
        }
        moduleCache.set(key, { payload, fetchedAt: Date.now() });
        setMetadata(payload);
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load report DSL metadata';
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [tenantId, siteId],
  );

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  return {
    metadata,
    loading,
    error,
    refresh: () => fetchMetadata({ refresh: true }),
  };
};

/** Drop the cached entry for a (tenant, site) — used by the wizard's manual refresh button. */
export const invalidateReportDslMetadata = (tenantId: string, siteId?: string | null): void => {
  moduleCache.delete(cacheKey(tenantId, siteId));
};
